import React, { useEffect, useState, useRef } from 'react';
import { ProfitDetailsResponse } from '../../../Interfaces/TeamAdminCoordDashboard';

interface ProfitCircleProps{
    profit: number;
    total_sales: number;
    width: number;
    height: number;
}

const ProfitCircle: React.FC<ProfitCircleProps> = ({
  profit,
  total_sales,
  width,
  height
}) => {
    
  const [radius, setRadius] = useState(100);
  const [strokeOffset, setStrokeOffset] = useState(0);
  const [fontSize, setFontSize] = useState(12); // Default font size
  const [strokeWidth, setStrokeWidth] = useState(10); // Default font size


  // setting the progress to be 1 if total_sales is 0 just so circle is purple not pink (if 0 its pink)
  const progress = Math.min(profit / total_sales || 1, 1); // Cap progress at 1 (100%)


  const updateRadius = () => {
    if (width > 0 && height > 0) {
      // Dynamically set radius based on container's width
      const newRadius = width / 2; // Adjust this ratio as needed
      setRadius(newRadius);
      const newFontSize = Math.max(newRadius / 8, 6); // Set a minimum font size of 12
      setFontSize(newFontSize);
      const newStrokeWidth = Math.max(newRadius / 8, 10); // Set a minimum font size of 12
      setStrokeWidth(newStrokeWidth);
    }
  };

  useEffect(() => {
    // Update the circle radius dynamically based on container width
    
    // + initial update on component mount
    updateRadius();

  }, [profit, total_sales, width, height]);

  const normalizedRadius = radius - strokeWidth / 2; // Adjust radius to fit within stroke
  const circumference = 2 * Math.PI * normalizedRadius; // Circumference of the circle

  // Update strokeDashoffset when progress or radius changes
  useEffect(() => {
    setStrokeOffset(circumference - progress * circumference); // Calculate offset
  }, [progress, circumference]);

  const getFontSizeForTotalSales = (totalSales: number) => {
    const digitCount = totalSales.toString().length + 2; // + 2 for decimal places
    let newFontSize = fontSize * 3.5; // Default font size is based on radius
    if (digitCount > 3) {
      // For each digit above 2, decrease the font size by 10% so still fits in circle
      const decreaseFactor = 0.9 ** (digitCount - 3); // Decrease by 10% per digit
      newFontSize *= decreaseFactor;
    }
    return newFontSize;
  };

  return (
    <div style={{ width: '100%', margin: '0 auto' }}>
      <header style={{ fontWeight: '700', paddingBottom: '5%', fontSize: fontSize*1.25}}>
        PROFIT DETAILS
      </header>
      <svg width={radius * 2} height={radius * 2}>
        {/* Group to apply scaleY(-1) only to circles with translation to keep them centered */}
        <g style={{ transform: 'scaleY(-1)', transformOrigin: 'center', translate: `0, ${radius * 2}` }}>
          {/* Background circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke="#ea91bd"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={radius}
            cy={radius}
            r={normalizedRadius}
            fill="transparent"
            stroke="#9174ec"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference} // Total length of the circle
            strokeDashoffset={strokeOffset} // How much of the circle to "hide"
            strokeLinecap="round" // Smooth ends
          />
        </g>
        <text
          x="39%"
          y="35%"
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize={fontSize} 
          fill="black" 
          fontWeight="bold" 
        >
          Total sales
        </text>
        <text
          x="50%"   // Horizontal position at 50% of the SVG width (centered)
          y="53%"   // Vertical position below "Total sales"
          alignmentBaseline="middle" 
          textAnchor="middle" 
          fontSize={getFontSizeForTotalSales(total_sales)} // Dynamically adjusted font size
          fill="navy" 
          fontWeight="800" 
          letterSpacing="0"
        >
          ${total_sales.toFixed(2)}
        </text>
      </svg>
      <div style={{ textAlign: 'center', marginTop: '20px', width: '100%'}}>
        {/* Legend Section */}
        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* Profit Legend */}
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '10px',
            fontSize: fontSize}}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#9174ec',
                  borderRadius: '50%',
                  marginRight: '5px',
                }}
              ></div>
              <div>Profit</div>
            </div>
            <div style={{ color: 'black', fontWeight: 'bold' }}>
              ${profit.toFixed(2)}
            </div>
          </div>

          {/* Total Sales Legend */}
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '10px',
            fontSize: fontSize}}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#ea91bd',
                  borderRadius: '50%',
                  marginRight: '5px',
                }}
              ></div>
              <div>Total Sales</div>
            </div>
            <div style={{ color: 'black', fontWeight: 'bold' }}>
              ${total_sales.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitCircle;
